<template>
  <!--
    Loading state: the order fetch (direct/email link) or the totals fetch is
    in flight. Held until the server's totals settle so the page never shows
    the browser's figure and then swaps it for the server's (#481).
  -->
  <div
    v-if="fetchLoading || totalsLoading"
    class="d-flex justify-center mt-5 pt-5"
    data-testid="order-confirmation-loading"
  >
    <v-card max-width="600" class="pa-8 text-center" elevation="2">
      <p class="text-body-1 text-grey-darken-1">Loading order details...</p>
    </v-card>
  </div>

  <!-- Error state: fetch failed (e.g. expired link, wrong account). -->
  <div
    v-else-if="fetchError"
    class="d-flex justify-center mt-5 pt-5"
    data-testid="order-confirmation-error"
  >
    <v-card max-width="600" class="pa-8 text-center" elevation="2">
      <p class="text-body-1 text-grey-darken-1">
        Your order was placed, but we couldn't retrieve the details.
        Check your order history or contact support if you need assistance.
      </p>
      <v-btn
        color="primary"
        class="mt-4"
        @click="continueShopping"
        data-testid="order-confirmation-continue"
      >
        Continue Shopping
      </v-btn>
    </v-card>
  </div>

  <!-- Main card: shown once we have data (from query params or backend fetch). -->
  <div
    v-else
    class="d-flex justify-center mt-5 pt-5"
    data-testid="order-confirmation"
  >
    <v-card max-width="600" class="pa-8 text-center" elevation="2">
      <v-icon size="64" color="success" class="mb-4" icon="check_circle" />
      <h3 class="text-h5 mb-4">Your order has been placed!</h3>

      <div v-if="orderNumber" class="mb-3">
        <div class="text-subtitle-1 text-grey">Order Number</div>
        <!--
          Rendered BARE, no "#" prefix. order_number is a complete
          self-describing identifier ("AC-2026-01001"); "#AC-2026-01001" is
          noise the shopper would have to strip when quoting it to support.
          Matches the two other surfaces that render this column,
          OrderDetailView.vue and portal/Orders.vue.
        -->
        <div
          class="text-h6 font-weight-bold"
          data-testid="confirmation-order-number"
        >
          {{ orderNumber }}
        </div>
      </div>

      <v-divider v-if="itemCount || orderTotal || totals" class="my-4" />

      <v-row v-if="itemCount || (orderTotal && !totals)" class="mb-4">
        <v-col v-if="itemCount" cols="6">
          <div class="text-subtitle-1 text-grey">Items</div>
          <div class="text-h6">{{ itemCount }}</div>
        </v-col>
        <!--
          This figure is the ITEMS only (tax, fees, shipping and any discount
          come after it), so it is labeled Subtotal. Calling it "Total" put
          $7.40 beside an Amount Charged of $8.76 and read as two totals for
          one order (card #396). Amount Charged below is the real total.
        -->
        <v-col
          v-if="orderTotal && !totals"
          cols="6"
          data-testid="confirmation-subtotal"
        >
          <div class="text-subtitle-1 text-grey">Subtotal</div>
          <div class="text-h6">${{ orderTotal }}</div>
        </v-col>
      </v-row>

      <!--
        Highest-priced item free (https://trello.com/c/T756dpsG). Shown as
        its own line, between the sub-total and the amount charged, so the
        numbers foot in the order the shopper reads them:

            Subtotal         $432.00
            Free item       -$135.00
            Amount Charged   $297.00

        Without it the page showed 432.00 and 297.00 with nothing
        explaining the gap, which reads as being overcharged -- and this is
        the last screen of the flow. Wording matches CartSummary.vue and
        the confirmation email, which the shopper has already seen.
      -->
      <!--
        THE server breakdown (GET /orders/<id>/totals, Bug Intake #481): the
        same lines, in the same order, as the cart and the confirmation
        email, honoring the store's "Consolidate Tax & Fees" setting. When it
        has loaded it REPLACES the item/free-item/allowance figures below,
        which are only the fallback for a breakdown that could not be read.
      -->
      <div v-if="totals" class="mb-4" data-testid="confirmation-breakdown">
        <div
          v-for="line in totals.lines"
          :key="line.key"
          class="d-flex justify-space-between text-body-1"
          :data-testid="`confirmation-line-${line.key}`"
        >
          <span class="text-grey-darken-1">{{ line.label }}</span>
          <span>{{ line.amount < 0 ? "-" : "" }}${{ formatAmount(line.amount) }}</span>
        </div>
      </div>

      <div
        v-if="orderFreeItemDiscount && !totals"
        class="mb-4"
        data-testid="confirmation-free-item-discount"
      >
        <div class="text-subtitle-1 text-grey">Free item</div>
        <div class="text-h6 text-success">-${{ orderFreeItemDiscount }}</div>
      </div>

      <div v-if="orderAllowance && !totals" class="mb-4">
        <div class="text-subtitle-1 text-grey">Allowance Applied</div>
        <div class="text-h6 text-success">-${{ orderAllowance }}</div>
      </div>

      <!--
        A PAYROLL / invoice or NET CHECKOUT order is not card-charged: its
        figure is what the employer deducts, or what is billed later, so it
        is labeled "Total" and names the payment method, never "Amount
        Charged" / "Amount to Be Charged" (Bug Intake #481, #518).
      -->
      <div
        v-if="chargedDisplay"
        class="mb-4"
        data-testid="confirmation-charged"
      >
        <div class="text-subtitle-1 text-grey">{{ chargedLabel }}</div>
        <div class="text-h6">${{ chargedDisplay }}</div>
        <div
          v-if="isNonCard"
          class="text-body-2 text-grey-darken-1"
          data-testid="confirmation-payment-method"
        >
          {{ paymentText }}
        </div>
        <div
          v-if="chargeDeferred && !isNonCard"
          class="text-body-2 text-grey-darken-1"
          data-testid="confirmation-charge-deferred-note"
        >
          Your card has not been charged yet. It will be charged when this
          store sends its orders to production.
        </div>
      </div>

      <!--
        Where a store-PRESET ship-to goes (card #402): the server's
        "Ships to <name, address>, set by <store>" sentence. Nothing for a
        shopper-entered address.
      -->
      <order-ship-to-notice
        :ship-to="shipTo"
        testid-prefix="confirmation-ship-to"
        class="mb-4"
      />

      <p class="text-body-1 text-grey-darken-1 mt-2">
        A confirmation email will be sent to you shortly.
      </p>

      <v-btn
        color="primary"
        class="mt-4"
        @click="continueShopping"
        data-testid="order-confirmation-continue"
      >
        Continue Shopping
      </v-btn>
    </v-card>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
// PR-R5: storefront URL restructure -- `/shop/:id` -> `/store/:id`,
// `/order_confirmation` -> `/store/:slug/orders/:id/confirmation`.
//
// Plan 2 section 2.5: when the shopper arrives via an email link or
// navigates directly, the query params are absent and the page falls
// back to fetching the order (same jv/get pattern + isOwnOrder guard
// as StorefrontOrderDetail.load). Display mapping lives in pure
// helpers so it stays testable without mounting Vuetify.
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import {
  hasQuerySummary,
  summaryFromQuery,
  summaryFromOrder,
} from "./orderConfirmationHelpers";
import { isOwnOrder } from "./orderDetailHelpers";
import OrderShipToNotice from "@/components/Order/OrderShipToNotice.vue";
import { fetchOrderShipTo } from "@/components/Order/orderShipTo";
import {
  fetchOrderTotals,
  formatAmount,
  isNonCardTotals,
  paymentLine,
} from "@/components/Order/orderTotals";

export default {
  components: { OrderShipToNotice },

  data() {
    // If no orderNumber query param is present, fetchOrder() will be called in
    // mounted(). Pre-set fetchLoading true so the INITIAL render shows the
    // loading state immediately -- avoiding a flash of the empty main card.
    const willFetch = !hasQuerySummary(this.$route?.query);
    return {
      // Populated by the backend fetch when query params are absent.
      fetchedSummary: null,
      // THE money breakdown (GET /orders/<id>/totals). Null until loaded,
      // and if it cannot be read; the summary figures are the fallback.
      totals: null,
      // What the shopper is told about where the order ships (GET
      // /orders/<id>/ship-to). Null until loaded and when it cannot be read.
      shipTo: null,
      totalsLoading: true,
      fetchLoading: willFetch,
      fetchError: false,
    };
  },

  computed: {
    // Query params (post-checkout redirect) win; the backend-fetched
    // summary is the direct/email-link fallback.
    summary() {
      if (hasQuerySummary(this.$route.query)) {
        return summaryFromQuery(this.$route.query);
      }
      return this.fetchedSummary || summaryFromOrder(null, null);
    },
    orderNumber() {
      return this.summary.orderNumber;
    },
    itemCount() {
      return this.summary.itemCount;
    },
    orderTotal() {
      return this.summary.orderTotal;
    },
    orderAllowance() {
      return this.summary.orderAllowance;
    },
    orderFreeItemDiscount() {
      return this.summary.orderFreeItemDiscount;
    },
    orderCharged() {
      return this.summary.orderCharged;
    },
    chargeDeferred() {
      return Boolean(this.summary.chargeDeferred);
    },
    isNonCard() {
      return isNonCardTotals(this.totals);
    },
    paymentText() {
      return paymentLine(this.totals);
    },
    // The server total wins over the summary's figure: it is the one number
    // the cart, the email and the order pages show for this order.
    chargedDisplay() {
      if (this.totals) return formatAmount(this.totals.total);
      return this.orderCharged;
    },
    chargedLabel() {
      if (this.isNonCard) return "Total";
      return this.chargeDeferred ? "Amount to Be Charged" : "Amount Charged";
    },
  },

  mounted() {
    // If the shopper arrived via the post-checkout redirect, query
    // params carry everything we need. Only hit the backend when they
    // are absent (direct link / email).
    if (!hasQuerySummary(this.$route.query)) {
      this.fetchOrder();
    }
    this.fetchTotals();
    this.fetchShipTo();
  },

  methods: {
    formatAmount,

    // Always fetched, on the redirect path too: the query params carry what
    // the BROWSER computed, and the page must show what the server froze.
    async fetchTotals() {
      const orderId = this.$route?.params?.id;
      try {
        this.totals = await fetchOrderTotals(this.$axios, orderId);
      } finally {
        this.totalsLoading = false;
      }
    },

    // Always fetched, like the totals: the page must say what the server
    // froze for this order. A failed read leaves the rest of the page alone.
    async fetchShipTo() {
      this.shipTo = await fetchOrderShipTo(
        this.$axios,
        this.$route?.params?.id,
      );
    },

    async fetchOrder() {
      const orderId = this.$route?.params?.id;
      if (!orderId) return;
      this.fetchLoading = true;
      this.fetchError = false;
      try {
        // Backend /api/orders/<id> is account-scoped for Shoppers via
        // RoleMixin._campaign_perms; the isOwnOrder check mirrors the
        // defense-in-depth guard in StorefrontOrderDetail.load().
        const order = await this.$store.dispatch("jv/get", [
          `/orders/${orderId}`,
          { params: { include: "account" } },
        ]);
        const myAccountId = this.$store.state.account?._jv?.id;
        if (!isOwnOrder(order, myAccountId)) {
          this.fetchError = true;
          return;
        }
        // The 2nd array element (axios config) is REQUIRED: jsonapi-vuex's
        // `get` action reads `config['data']` unconditionally, so a
        // single-element array leaves `config` undefined and throws
        // `TypeError: Cannot read properties of undefined (reading 'data')`
        // BEFORE the XHR -- caught below and rendered as the error UI even for
        // a genuinely-owned order (card nG0ZR8TT). Pass `{}` to mirror the
        // order fetch above and StorefrontOrderDetail.load().
        const orderItems = await this.$store.dispatch("jv/get", [
          `/orders/${orderId}/order_items`,
          {},
        ]);
        this.fetchedSummary = summaryFromOrder(
          order,
          Object.values(orderItems || {})
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load order for confirmation page", describeError(err));
        this.fetchError = true;
      } finally {
        this.fetchLoading = false;
      }
    },

    continueShopping() {
      // Convergence (card q7EQ3a0b): "Continue Shopping" returns to the NEW
      // storefront Home (/shop/<slug>), NOT the legacy single-page
      // /store/<slug>. The slug resolver handles the :slug route param and
      // the legacy localStorage.storeUrl.
      // Phase D (ADR 0059): the no-slug fallback push to bare `/shop` no
      // longer mounts a page directly -- it redirects (router/redirects.js)
      // via the same localStorage.storeUrl lookup resolveStorefrontSlug
      // already tried, so it lands on /login when no store context exists.
      const slug = resolveStorefrontSlug(this.$route);
      this.$router.push(slug ? `/shop/${slug}` : "/shop");
    },
  },
};
</script>
